<!-- Ein durchgehender gap-4-Stapel über Datei-Header, Positions-Header und Karten (Entwurf:
     16 px zwischen allen Elementen, keine Trennlinien). -->
<div #scrollContainer class="flex-1 min-h-0 flex flex-col w-full gap-4 overflow-y-auto">

  @for (group of model.groups; track group.node?.key ?? $index) {

    <!-- Datei-Header (bei rules-first: Prüfregel-Header) -->
    @if (group.node && isGroupRendered(group)) {
      <ng-container
        [ngTemplateOutletContext]="{node: group.node, counts: group.counts, level: 'group'}"
        [ngTemplateOutlet]="groupHeader">
      </ng-container>
    }

    @for (section of group.sections; track section.node?.key ?? $index) {

      <!-- Positions-Header (Strukturebene, z. B. LV-Position) -->
      @if (section.node && isSectionRendered(section)) {
        <ng-container
          [ngTemplateOutletContext]="{node: section.node, counts: section.counts, level: 'section'}"
          [ngTemplateOutlet]="groupHeader">
        </ng-container>
      }

      <!--
        Nur die Karten des aktuellen Render-Fensters werden aufgebaut; der Rest kommt beim Scrollen
        nach (siehe `isRendered` in der Komponente). Ein KI-Bericht bringt je Datei × Prüfregel eine
        Karte mit – zuvor landeten sie beim Öffnen alle auf einmal im DOM, aufgeklappt zusätzlich
        mit dem Markdown-Rendering jedes Freitext-Blocks.

        Die beiden Überschriften oben hängen am selben Fenster (`isGroupRendered`/
        `isSectionRendered`): eine Überschrift ohne eine einzige Karte darunter ist eine Lücke,
        und sie verfälscht zusätzlich die Höhe, an der das Nachladen ansetzt.
      -->
      @for (item of section.items; track item.node.key) {
        @if (isRendered(item.node.key)) {
          <ng-container [ngTemplateOutletContext]="{item: item}" [ngTemplateOutlet]="cardTemplate">
          </ng-container>
        }
      }
    }
  } @empty {
    <app-empty-state [icon]="faFileCircleXmark"
                     message="Keine Prüfergebnisse für die aktuelle Auswahl."></app-empty-state>
  }
</div>

<!-- Eine Prüfregel-Karte: Kopfzeile plus (aufgeklappt) die Ergebnis-Blöcke. -->
<ng-template #cardTemplate let-item="item">
  @let expanded = isExpanded(item.node.key);

  <div class="report-card w-full flex flex-row bg-white border border-gray-200 rounded-lg">

    <!-- 6px-Statusstreifen -->
    @if (item.node.showStatus) {
      <div [class]="stripeClass(item.node.status)" class="w-1.5 shrink-0 self-stretch rounded-l-lg"></div>
    }

    <div class="flex-1 min-w-0 flex flex-col">

      <!-- Karten-Header -->
      <div (click)="toggleItem.emit(item.node.key)"
           class="w-full flex flex-row items-center gap-2 px-4 py-3 cursor-pointer select-none">

        @if (item.node.badge) {
          <ng-container
            [ngTemplateOutletContext]="{badge: item.node.badge}"
            [ngTemplateOutlet]="badgeTemplate">
          </ng-container>
        }

        <span class="text-sm font-medium text-gray-900 shrink-0">{{ item.node.title }}</span>

        @if (item.node.secondaryText) {
          <span class="text-sm text-gray-500 shrink-0">{{ item.node.secondaryText }}</span>
        }

        @for (chip of item.node.chips; track $index) {
          <bic-badge [tooltip]="chipTooltip([chip])" type="info">
            {{ chip.label ? chip.label + ': ' + chip.value : chip.value }}
          </bic-badge>
        }

        <!-- Beschreibung füllt den Rest; ohne Beschreibung schiebt der Spacer das Chevron nach rechts. -->
        @if (item.node.description) {
          <span [tooltip]="item.node.description"
                class="flex-1 min-w-0 text-sm text-gray-500 truncate">{{ item.node.description }}</span>
        } @else {
          <span class="flex-1 min-w-0"></span>
        }

        @for (meta of item.node.meta; track meta.key) {
          <span [tooltip]="meta.label"
                class="text-sm text-gray-500 whitespace-nowrap shrink-0">{{ meta.value }}</span>
        }

        <fa-icon [icon]="expanded ? faChevronUp : faChevronDown"
                 class="text-gray-500 shrink-0 text-svg-16"></fa-icon>
      </div>

      <!-- Karten-Body -->
      @if (expanded) {
        @for (block of item.body; track block.key) {
          @switch (block.kind) {

            <!-- Prüfschritt (KI/Hybrid) -->
            @case ('step') {
            <div appReportSourceLinks class="w-full flex flex-col gap-2 px-4 py-3">
                @if (block.heading || block.status) {
                  <div class="flex flex-row items-center gap-2">
                    @if (block.status) {
                      <fa-icon [class]="statusTextClass(block.status)"
                               [icon]="statusIcon(block.status)"
                               class="shrink-0 text-svg-16"></fa-icon>
                    }
                    @if (block.heading) {
                      <span class="text-sm font-medium text-gray-500">{{ block.heading }}</span>
                    }
                    @if (block.ratingText) {
                      <span class="text-sm text-gray-400">{{ block.ratingText }}</span>
                    }
                  </div>
                }
                @for (entry of block.entries; track entry.key) {
                  @if (entry.label) {
                    <!-- Benanntes Feld, z. B. „Handlungsempfehlung: …" -->
                    <div class="text-sm font-medium text-gray-500 break-words">
                      <span>{{ entry.label }}:</span>
                    <markdown
                      [data]="entry.value | reportSourceLinks:block.sourceFiles:sourceLinkProjectId | normalizeMarkdown"
                      class="report-answer-markdown report-answer-text"></markdown>
                    </div>
                  } @else {
                    <!-- KI-Antworttext: Überschrift steht schon oben, daher ohne Label -->
                    <markdown
                      [data]="entry.value | reportSourceLinks:block.sourceFiles:sourceLinkProjectId | normalizeMarkdown"
                      class="text-sm text-gray-900 break-words report-answer-markdown report-answer-text"></markdown>
                  }
                }
              </div>
            }

            <!-- Facette (IDS) -->
            @case ('facet') {
              <div class="group w-full flex flex-col gap-1 px-4 py-2 hover:bg-gray-50">
              <div class="w-full flex flex-row items-center gap-4">
                @if (block.percentText) {
                  <ng-container
                    [ngTemplateOutletContext]="{
                      badge: {text: block.percentText, type: block.badgeType, fixedWidth: true}
                    }"
                    [ngTemplateOutlet]="badgeTemplate">
                  </ng-container>
                }
                <span class="text-sm font-medium text-gray-900">{{ block.title }}</span>
                @if (block.chips.length > 0) {
                  <fa-icon [icon]="faCircleInfo" [tooltip]="chipTooltip(block.chips)"
                           class="text-gray-400 shrink-0 text-svg-16"></fa-icon>
                }
                @if (block.secondaryText) {
                  <span class="text-sm text-gray-500 whitespace-nowrap">{{ block.secondaryText }}</span>
                }
                <div class="flex-1 min-w-0 flex flex-row justify-end">
                  <app-report-row-actions (copyClick)="copyFacetResponse(item.node.title, block.title)"
                                           (taskClick)="requestTaskCreation(item.node.title, block.title)">
                  </app-report-row-actions>
                </div>
              </div>
                <!-- Warum die Anforderung gerissen ist. Muss dieselben Saetze zeigen wie die
                     Spaltenansicht, deshalb kommt der Text aus formatFailureReason. -->
                @if (block.reasonTexts && block.reasonTexts.length > 0) {
                  <div class="flex flex-col gap-0.5">
                    @for (reasonText of block.reasonTexts; track reasonText) {
                      <span class="text-xs text-gray-500">{{ reasonText }}</span>
                    }
                  </div>
                }
              </div>
            }

            <!-- Regelweite Prüfparameter: einmal je Karte, nicht in jedem Prüfschritt -->
            @case ('params') {
              <div class="w-full flex flex-col gap-2 px-4 py-3 border-t border-gray-100">
                <div class="text-xs font-medium text-gray-500 uppercase">{{ block.label }}</div>
                @for (entry of block.entries; track entry.key) {
                  <div class="text-sm break-words">
                    <span class="font-medium text-gray-500">{{ entry.label }}: </span>
                    <markdown [data]="entry.value | normalizeMarkdown"
                              class="text-gray-900 report-answer-markdown report-answer-text"></markdown>
                  </div>
                }
              </div>
            }

            <!-- Roh-JSON (nur für nicht aufbereitete Prüfungsarten) -->
            @case ('raw') {
              <div class="w-full px-4 py-3">
                <pre
                  class="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-mono text-gray-700">{{ block.json | json }}</pre>
              </div>
            }
          }
        } @empty {
          <div class="w-full px-4 py-3 text-sm text-gray-400">Keine weiteren Details verfügbar.</div>
        }
      }
    </div>
  </div>
</ng-template>

<!--
  Badge im Karten-/Gruppen-Header.

  Prozent-Badges sind laut Entwurf 50 px breit, damit die Karten untereinander fluchten. Die Breite
  sitzt bewusst auf dem Text und nicht auf `bic-badge`: dessen innerer Container ließe sich nur über
  eine Kind-Selektor-Variante dehnen, und die generiert Tailwind aus einem Angular-Attributwert nicht
  (das `>` im Klassennamen bricht den Extractor). 36 px Text (`w-9`) + 2 × 10 px Innenabstand von
  bic-badge = 56 px. Der Entwurf sah 50 px vor – darin war „100%" aber abgeschnitten (gemeldet
  2026-10-06), die breiteste vorkommende Angabe muss ganz hineinpassen.
-->
<ng-template #badgeTemplate let-badge="badge">
  <bic-badge [bgClass]="badgeBgClass(badge.type)" [textClass]="badgeTextClass(badge.type)"
             [type]="badge.type">
    @if (badge.fixedWidth) {
      <span class="inline-block w-9 whitespace-nowrap text-center">{{ badge.text }}</span>
    } @else {
      {{ badge.text }}
    }
  </bic-badge>
</ng-template>

<!-- Gemeinsame Kopfzeile für Datei- und Positions-Ebene: derselbe Aufbau, nur andere Textgröße. -->
<ng-template #groupHeader let-counts="counts" let-level="level" let-node="node">
  <div [class.pt-2]="level === 'section'" class="w-full flex flex-row items-center gap-2.5">
    @if (node.badge) {
      <ng-container
        [ngTemplateOutletContext]="{badge: node.badge}"
        [ngTemplateOutlet]="badgeTemplate">
      </ng-container>
    }

    <span [class]="level === 'group' ? 'text-base' : 'text-sm'"
          class="flex-1 min-w-0 break-words font-medium text-gray-900">{{ node.title }}</span>

    @if (node.description) {
      <span [tooltip]="node.description" class="text-sm text-gray-500 truncate">{{ node.description }}</span>
    }

    @for (meta of node.meta; track meta.key) {
      <span [tooltip]="meta.label" class="text-sm text-gray-500 whitespace-nowrap shrink-0">{{ meta.value }}</span>
    }

    @for (count of counts; track count.type) {
      <bic-badge [bgClass]="badgeBgClass(count.type)" [textClass]="badgeTextClass(count.type)"
                 [type]="count.type">
        <fa-icon [icon]="statusIcon(count.type)" iconLeft></fa-icon>
        {{ count.label }}
      </bic-badge>
    }
  </div>
</ng-template>
